import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Sparkles, Loader2, ImageIcon, Type } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API_URL as API } from '@/utils/apiConfig';
import { toast } from 'sonner';
import axios from 'axios';
import html2canvas from 'html2canvas';

export default function PostToFeedModal({ message, conversationTitle, onClose }) {
  const { t } = useTranslation();
  const [content, setContent] = useState('');
  const [mode, setMode] = useState('text'); // 'text' or 'image'
  const [posting, setPosting] = useState(false);
  const cardRef = useRef(null);

  const generateImageData = async () => {
    if (!cardRef.current) return null;
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2, useCORS: true, backgroundColor: null, logging: false,
      });
      return canvas.toDataURL('image/png');
    } catch {
      return null;
    }
  };

  const handlePost = async () => {
    setPosting(true);
    try {
      const payload = {
        content: content.trim(),
        // Always store eili_response data (needed for Ask Eili + image tap reveal)
        eili_response: {
          content: message.content,
          question: message._question || '',
          conversation_title: conversationTitle || '',
        },
      };

      if (mode === 'image') {
        const imageData = await generateImageData();
        if (imageData) payload.image_data = imageData;
      }

      await axios.post(`${API}/feed/posts`, payload, { withCredentials: true });
      toast.success(t('share.posted'));
      onClose();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : t('share.failed_post'));
    }
    setPosting(false);
  };

  const preview = (message.content || '').replace(/[#*_`]/g, '').slice(0, 120);

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative w-full sm:max-w-md mx-auto rounded-t-2xl sm:rounded-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
        style={{ background: 'var(--theme-surface)', border: '1px solid var(--glass-border)' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="post-to-feed-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid var(--glass-border)' }}>
          <h3 className="text-base font-semibold" style={{ color: 'var(--theme-text)' }}>{t('generated.posttofeedmodal_post_to_feed_e742')}</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-[var(--theme-hover)]" data-testid="close-post-feed-modal">
            <X className="h-5 w-5" style={{ color: 'var(--theme-textSecondary)' }} />
          </button>
        </div>

        {/* Text input */}
        <div className="px-5 pt-4 pb-2">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={t('share.add_thoughts')}
            rows={2}
            className="w-full bg-transparent text-sm outline-none resize-none"
            style={{ color: 'var(--theme-text)' }}
            autoFocus
            data-testid="post-feed-text-input"
          />
        </div>

        {/* Eili response preview */}
        <div className="mx-5 mb-3 rounded-xl p-3" style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)' }}>
          <div className="flex items-center gap-1.5 mb-1">
            <Sparkles className="h-3 w-3" style={{ color: 'var(--theme-accent)' }} />
            <span className="text-[11px] font-medium" style={{ color: 'var(--theme-accent)' }}>{t('generated.posttofeedmodal_eili_response_595a')}</span>
          </div>
          <p className="text-xs line-clamp-3" style={{ color: 'var(--theme-text)' }}>
            {preview}{message.content?.length > 120 ? '...' : ''}
          </p>
        </div>

        {/* Share as: Image or Text toggle */}
        <div className="px-5 pb-3">
          <p className="text-[11px] font-medium mb-2" style={{ color: 'var(--theme-textSecondary)' }}>{t('generated.posttofeedmodal_share_as_88f2')}</p>
          <div className="flex gap-2">
            <button
              onClick={() => setMode('text')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all`}
              style={
                mode === 'text'
                  ? { background: 'var(--theme-accent)', color: '#fff' }
                  : { background: 'var(--glass-bg)', color: 'var(--theme-textSecondary)' }
              }
              data-testid="mode-text"
            >
              <Type className="h-3.5 w-3.5" />
              {t('generated.posttofeedmodal_text_ca42')}
            </button>
            <button
              onClick={() => setMode('image')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all`}
              style={
                mode === 'image'
                  ? { background: 'var(--theme-accent)', color: '#fff' }
                  : { background: 'var(--glass-bg)', color: 'var(--theme-textSecondary)' }
              }
              data-testid="mode-image"
            >
              <ImageIcon className="h-3.5 w-3.5" />
              {t('generated.posttofeedmodal_image_fee0')}
            </button>
          </div>
        </div>

        {/* Post button */}
        <div className="px-5 pb-4 flex justify-end">
          <Button
            className="h-9 rounded-full text-sm px-6"
            style={{ background: 'var(--theme-accent)', color: '#fff' }}
            onClick={handlePost}
            disabled={posting}
            data-testid="submit-post-feed-btn"
          >
            {posting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            {posting ? t('share.posting') : t('share.post')}
          </Button>
        </div>

        <div className="h-1" style={{ paddingBottom: 'max(0.25rem, var(--safe-area-bottom, env(safe-area-inset-bottom, 0px)))' }} />
      </div>
      {/* Hidden card for image generation */}
      {mode === 'image' && (
        <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          <div ref={cardRef} style={{ width: 440 }}>
            <EiliShareCard text={message.content} />
          </div>
        </div>
      )}
    </div>
  );
}

function EiliShareCard({ text }) {
  const {
    t
  } = useTranslation();

  const displayText = text.length > 500 ? text.slice(0, 500) + '...' : text;

  return (
    <div style={{ background: '#1a1a2e', padding: '32px 28px 24px', fontFamily: "'Roboto', -apple-system, sans-serif", minHeight: 200, position: 'relative' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: '#c9a44a' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
        <img src="/logo.png" alt={t('generated.posttofeedmodal_eili_23ec')} style={{ width: 28, height: 28, objectFit: 'contain' }} crossOrigin="anonymous" />
        <span style={{ fontFamily: "'Playwrite ES Deco', cursive", fontSize: 18, fontWeight: 700, color: '#c9a44a' }}>{t('generated.posttofeedmodal_eili_23ec')}</span>
        <span style={{ fontSize: 11, color: '#888', marginLeft: 4 }}>{t('generated.posttofeedmodal_the_word_first_dc04')}</span>
      </div>
      <div style={{ height: 1, background: '#333', marginBottom: 18 }} />
      <div style={{ fontSize: 14, lineHeight: 1.7, color: '#e0e0e0', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{displayText}</div>
      <div style={{ marginTop: 20, paddingTop: 14, borderTop: '1px solid #333', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 11, color: '#888' }}>{t('generated.posttofeedmodal_shared_via_eili_d9dd')}</span>
      </div>
    </div>
  );
}
